// src/features/medicine-inventory/components/stock/MultiMedicineIssueForm.jsx
import React, { useState } from 'react';
import { FaPlus, FaTrash, FaPrescription } from 'react-icons/fa';
import { useInventory } from '../../context/InventoryContext';
import { generatePrescriptionNumber } from '../../utils/helpers';

const MultiMedicineIssueForm = ({ onClose }) => {
  const { medicines, addStockMovement } = useInventory();
  const [formData, setFormData] = useState({
    patientName: '',
    doctorName: '',
    prescriptionNumber: generatePrescriptionNumber(),
    notes: '',
    medicines: [{
      medicineId: '',
      quantity: 1,
      unitPrice: ''
    }]
  });
  const [submitting, setSubmitting] = useState(false);

  const handleAddMedicine = () => {
    setFormData(prev => ({
      ...prev,
      medicines: [...prev.medicines, {
        medicineId: '',
        quantity: 1,
        unitPrice: ''
      }]
    }));
  };

  const handleRemoveMedicine = (index) => {
    setFormData(prev => ({
      ...prev,
      medicines: prev.medicines.filter((_, i) => i !== index)
    }));
  };

  const handleMedicineChange = (index, field, value) => {
    const updatedMedicines = [...formData.medicines];
    
    if (field === 'medicineId') {
      const selectedMedicine = medicines.find(m => m.id === value);
      updatedMedicines[index] = {
        ...updatedMedicines[index],
        medicineId: value,
        unitPrice: selectedMedicine?.price || '',
        maxStock: selectedMedicine?.currentStock || 0
      };
    } else {
      updatedMedicines[index][field] = value;
    }
    
    setFormData(prev => ({
      ...prev,
      medicines: updatedMedicines
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    
    try {
      // Validate each medicine has sufficient stock
      for (const item of formData.medicines) {
        const medicine = medicines.find(m => m.id === item.medicineId);
        if (medicine && item.quantity > medicine.currentStock) {
          alert(`Insufficient stock for ${medicine.name}. Available: ${medicine.currentStock}`);
          setSubmitting(false);
          return;
        }
      }

      // Create multi-medicine issue
      await addStockMovement({
        type: 'MULTI_ISSUE',
        ...formData
      });
      
      onClose();
    } catch (error) {
      console.error('Failed to create issue:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const calculateTotal = () => {
    return formData.medicines.reduce((sum, item) => {
      return sum + (parseInt(item.quantity || 0) * parseFloat(item.unitPrice || 0));
    }, 0);
  };

  const getMedicineStock = (medicineId) => {
    const medicine = medicines.find(m => m.id === medicineId);
    return medicine?.currentStock || 0;
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Patient Information */}
      <div className="bg-gray-50 p-4 rounded-lg">
        <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
          <FaPrescription className="mr-2 text-blue-600" />
          Prescription Details
        </h3>
        
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Patient Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={formData.patientName}
              onChange={(e) => setFormData({ ...formData, patientName: e.target.value })}
              placeholder="Enter patient name"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Doctor Name
            </label>
            <input
              type="text"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={formData.doctorName}
              onChange={(e) => setFormData({ ...formData, doctorName: e.target.value })}
              placeholder="Enter doctor name"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Prescription Number
            </label>
            <input
              type="text"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-gray-100"
              value={formData.prescriptionNumber}
              readOnly
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Notes
            </label>
            <input
              type="text"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              placeholder="Additional notes"
            />
          </div>
        </div>
      </div>

      {/* Medicines List */}
      <div className="bg-white rounded-lg border border-gray-200">
        <div className="px-4 py-3 border-b border-gray-200 flex justify-between items-center">
          <h3 className="text-lg font-medium text-gray-900">Medicines</h3>
          <button
            type="button"
            onClick={handleAddMedicine}
            className="inline-flex items-center px-3 py-1 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700"
          >
            <FaPlus className="mr-1" /> Add Medicine
          </button>
        </div>

        <div className="p-4 space-y-4">
          {formData.medicines.map((item, index) => {
            const medicine = medicines.find(m => m.id === item.medicineId);
            const availableStock = getMedicineStock(item.medicineId);
            
            return (
              <div key={index} className="flex items-start space-x-3 p-3 bg-gray-50 rounded-lg">
                <div className="flex-1 grid grid-cols-3 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Medicine
                    </label>
                    <select
                      required
                      className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      value={item.medicineId}
                      onChange={(e) => handleMedicineChange(index, 'medicineId', e.target.value)}
                    >
                      <option value="">Select Medicine</option>
                      {medicines.map(med => (
                        <option key={med.id} value={med.id}>
                          {med.name} - {med.manufacturer} (Stock: {med.currentStock} {med.unit}s)
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Quantity
                    </label>
                    <input
                      type="number"
                      required
                      min="1"
                      max={availableStock}
                      className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      value={item.quantity}
                      onChange={(e) => handleMedicineChange(index, 'quantity', parseInt(e.target.value))}
                    />
                    {medicine && (
                      <p className="text-xs text-gray-500 mt-1">
                        Available: {availableStock} {medicine.unit}s
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Unit Price ($)
                    </label>
                    <input
                      type="number"
                      required
                      min="0"
                      step="0.01"
                      className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-gray-100"
                      value={item.unitPrice}
                      readOnly
                    />
                  </div>
                </div>

                {formData.medicines.length > 1 && (
                  <button
                    type="button"
                    onClick={() => handleRemoveMedicine(index)}
                    className="mt-5 text-red-600 hover:text-red-800"
                  >
                    <FaTrash />
                  </button>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Summary */}
      <div className="bg-blue-50 p-4 rounded-lg">
        <div className="flex justify-between items-center">
          <span className="font-medium text-gray-700">Total Amount:</span>
          <span className="text-2xl font-bold text-blue-600">
            ${calculateTotal().toFixed(2)}
          </span>
        </div>
      </div>

      {/* Form Actions */}
      <div className="flex justify-end space-x-3 pt-4">
        <button
          type="button"
          onClick={onClose}
          className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
          disabled={submitting}
        >
          Cancel
        </button>
        <button
          type="submit"
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          disabled={submitting || formData.medicines.length === 0}
        >
          {submitting ? 'Processing...' : 'Issue Medicines'}
        </button>
      </div>
    </form>
  );
};

export default MultiMedicineIssueForm;